import React, { FC, HTMLAttributes, useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { Dropdown } from 'components/ui';
import Fieldgroups from './Fieldgroups';
import { IOption } from 'components/ui/Dropdown/Dropdown';
import { ISelectFieldsConfig } from 'constants/eventOptions';
import styles from './SelectFields.module.scss';

export interface ISelectFieldsValues {
  name: string;
  types: string[];
}

export interface ISelectFields extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  name: string;
  value: ISelectFieldsValues[];
  options: IOption[];
  config: ISelectFieldsConfig;
  onChange?: (values: ISelectFieldsValues[]) => void;
  checkDisabled?: (type: string, values: string[]) => boolean;
  hasScrollFieldgroup?: boolean;
  disabled?: boolean;
  isSmallList?: boolean;
  addSelectedOptionInFront?: boolean;
  useRelativeTopElements?: boolean;
  isRemoveEnabled?: boolean;
  dropdownPlaceholder?: string;
}

const SelectFields: FC<ISelectFields> = (props) => {
  const {
    value,
    onChange = () => {},
    name,
    options,
    config,
    checkDisabled,
    disabled,
    className,
    isSmallList,
    hasScrollFieldgroup,
    isRemoveEnabled,
    addSelectedOptionInFront = false,
    useRelativeTopElements = false,
    dropdownPlaceholder,
  } = props;
  const dropdownValues = useMemo<string[]>(
    () => (Array.isArray(value) ? value.map((item) => item.name) : [value]),
    [value]
  );

  const rootClassName = classNames(styles.root, className);
  const fieldgroupsClassNames = [classNames({ [styles.fieldgroups]: hasScrollFieldgroup })];
  if (useRelativeTopElements) fieldgroupsClassNames.push(styles['fields-relative']);

  const handleOptionSelect = useCallback(
    (fieldName: string) => {
      const newTypes = config[fieldName][0] === 'READ' ? ['READ'] : [];
      const newValues = [...value];
      const valueToAdd = { name: fieldName, types: newTypes };
      addSelectedOptionInFront ? newValues.unshift(valueToAdd) : newValues.push(valueToAdd);
      onChange(newValues);
    },
    [config, onChange, value, addSelectedOptionInFront]
  );

  const handleFieldRemove = useCallback(
    (fieldName) => {
      onChange(value.filter((option) => option.name !== fieldName));
    },
    [onChange, value]
  );

  return (
    <div className={rootClassName}>
      <Dropdown
        options={options}
        onChange={handleOptionSelect}
        value={dropdownValues}
        disabled={disabled}
        isSmallList={isSmallList}
        useRelativeTop={useRelativeTopElements}
        placeholder={dropdownPlaceholder}
      />
      <Fieldgroups
        name={name}
        values={value}
        onFieldRemove={handleFieldRemove}
        config={config}
        disabled={disabled}
        isRemoveEnabled={isRemoveEnabled}
        checkDisabled={checkDisabled}
        className={fieldgroupsClassNames.join(' ')}
      />
    </div>
  );
};

export default SelectFields;
